import { useState } from 'react';
import { useQuery, useQueryClient } from 'react-query';
import { Link } from 'react-router-dom';
import { FileText, RefreshCw, CheckSquare, Square, AlertCircle, Download, ArrowRightLeft } from 'lucide-react';
import { customerAPI, agentAPI } from '../services/api';
import toast from 'react-hot-toast';
import api from '../services/api';

const Agent = () => {
  const queryClient = useQueryClient();
  const [selectedIds, setSelectedIds] = useState([]);
  const [generating, setGenerating] = useState(false);
  const [batchStatus, setBatchStatus] = useState('');
  const [updatingStatus, setUpdatingStatus] = useState(false);

  // Fetch Not Processed customers
  const { data, isLoading, refetch } = useQuery(
    ['agent-customers'],
    () => customerAPI.getAll({ status: 'Not Processed', limit: 200, sortBy: 'createdAt', sortOrder: 'DESC' }).then(res => res.data.data),
    { staleTime: 30 * 1000 }
  );

  // Fetch status options from settings
  const { data: customerSettings } = useQuery('customer-settings', async () => {
    const res = await api.get('/settings/customers');
    return res.data.data;
  });

  const customers = data?.customers || [];
  const statusOptions = customerSettings?.statusOptions || [
    'Not Processed', 'In Process', 'Pending Installation', 'Standby', 'Active', 'Cancelled'
  ];

  const toggleSelect = (id) => {
    setSelectedIds(prev =>
      prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]
    );
  };

  const toggleSelectAll = () => {
    if (selectedIds.length === customers.length) {
      setSelectedIds([]);
    } else {
      setSelectedIds(customers.map(c => c.id));
    }
  };

  const handleGenerateODT = async () => {
    if (selectedIds.length === 0) {
      toast.error('Please select at least one customer');
      return;
    }

    setGenerating(true);
    try {
      const response = await agentAPI.generateODT(selectedIds);

      // Extract filename from response headers
      const filename = response.headers['x-filename'] || 'Fiber Request.odt';
      const updatedCount = response.headers['x-updated-count'] || selectedIds.length;

      // Trigger browser download
      const blob = new Blob([response.data], { type: 'application/vnd.oasis.opendocument.text' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);

      toast.success(`Fiber Request generated! ${updatedCount} customer(s) moved to "In Process"`);
      setSelectedIds([]);

      // Refresh data
      queryClient.invalidateQueries('agent-customers');
      queryClient.invalidateQueries('customers');
    } catch (error) {
      const message = error.response?.data?.message || 'Failed to generate Fiber Request';
      toast.error(message);
    } finally {
      setGenerating(false);
    }
  };

  const handleBatchStatus = async () => {
    if (selectedIds.length === 0) {
      toast.error('Please select at least one customer');
      return;
    }
    if (!batchStatus) {
      toast.error('Please select a status');
      return;
    }

    setUpdatingStatus(true);
    try {
      const response = await agentAPI.batchUpdateStatus(selectedIds, batchStatus);
      toast.success(response.data.message);
      setSelectedIds([]);
      setBatchStatus('');

      queryClient.invalidateQueries('agent-customers');
      queryClient.invalidateQueries('customers');
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to update statuses');
    } finally {
      setUpdatingStatus(false);
    }
  };

  return (
    <div className="space-y-4 md:space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold text-gray-900">Agent</h1>
          <p className="text-gray-600 mt-1 text-sm md:text-base">Automated fiber request processing</p>
        </div>
        <button
          onClick={() => refetch()}
          className="btn btn-secondary flex items-center justify-center gap-2 w-full sm:w-auto"
        >
          <RefreshCw size={18} />
          Refresh
        </button>
      </div>

      {/* Action Bar */}
      <div className="card">
        <div className="flex flex-col md:flex-row gap-4 items-start md:items-end">
          {/* Generate ODT */}
          <div className="flex-1">
            <p className="text-sm font-medium text-gray-700 mb-2">
              Generate Fiber Request ({selectedIds.length} selected)
            </p>
            <button
              onClick={handleGenerateODT}
              disabled={generating || selectedIds.length === 0}
              className="btn btn-primary flex items-center gap-2 w-full md:w-auto disabled:opacity-50"
            >
              {generating ? (
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white" />
              ) : (
                <Download size={18} />
              )}
              {generating ? 'Generating...' : 'Generate Fiber Request'}
            </button>
          </div>

          {/* Batch Status Update */}
          <div className="flex-1">
            <p className="text-sm font-medium text-gray-700 mb-2">Batch Status Update</p>
            <div className="flex gap-2">
              <select
                className="input flex-1"
                value={batchStatus}
                onChange={(e) => setBatchStatus(e.target.value)}
              >
                <option value="">Select status...</option>
                {statusOptions.map(s => (
                  <option key={s} value={s}>{s}</option>
                ))}
              </select>
              <button
                onClick={handleBatchStatus}
                disabled={updatingStatus || selectedIds.length === 0 || !batchStatus}
                className="btn btn-secondary flex items-center gap-2 disabled:opacity-50"
              >
                {updatingStatus ? (
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-gray-600" />
                ) : (
                  <ArrowRightLeft size={18} />
                )}
                Update
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Customer Table */}
      <div className="card overflow-hidden p-0 md:p-0">
        {isLoading ? (
          <div className="flex justify-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-500"></div>
          </div>
        ) : customers.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-12 text-gray-500">
            <AlertCircle size={48} className="mb-4 text-gray-300" />
            <p className="text-lg font-medium">No "Not Processed" customers</p>
            <p className="text-sm mt-1">All customers have been processed</p>
          </div>
        ) : (
          <>
            {/* Mobile Card View */}
            <div className="block md:hidden divide-y divide-gray-200">
              <div
                className="p-4 bg-gray-50 flex items-center gap-3 cursor-pointer"
                onClick={toggleSelectAll}
              >
                {selectedIds.length === customers.length ? (
                  <CheckSquare size={20} className="text-primary-600" />
                ) : (
                  <Square size={20} className="text-gray-400" />
                )}
                <span className="font-medium text-sm text-gray-700">
                  Select All ({customers.length})
                </span>
              </div>
              {customers.map((customer) => (
                <div
                  key={customer.id}
                  className={`p-4 cursor-pointer transition-colors ${
                    selectedIds.includes(customer.id) ? 'bg-primary-50' : 'hover:bg-gray-50'
                  }`}
                  onClick={() => toggleSelect(customer.id)}
                >
                  <div className="flex items-start gap-3">
                    {selectedIds.includes(customer.id) ? (
                      <CheckSquare size={20} className="text-primary-600 mt-0.5 flex-shrink-0" />
                    ) : (
                      <Square size={20} className="text-gray-400 mt-0.5 flex-shrink-0" />
                    )}
                    <div className="flex-1 min-w-0">
                      <p className="font-semibold text-gray-900 truncate">{customer.customerName}</p>
                      {customer.fullAddress && (
                        <p className="text-sm text-gray-600 mt-1 line-clamp-2">{customer.fullAddress}</p>
                      )}
                      <div className="flex flex-wrap gap-2 mt-2 text-xs text-gray-500">
                        {customer.cytaPlan && <span className="bg-gray-100 px-2 py-0.5 rounded">{customer.cytaPlan}</span>}
                        {customer.area && <span className="bg-gray-100 px-2 py-0.5 rounded">{customer.area}</span>}
                        {customer.cytaFiberCode && <span className="bg-green-50 text-green-700 px-2 py-0.5 rounded font-mono text-xs">{customer.cytaFiberCode}</span>}
                        {!customer.cytaFiberCode && <span className="bg-yellow-50 text-yellow-700 px-2 py-0.5 rounded">No fiber code</span>}
                      </div>
                    </div>
                    <Link
                      to={`/customers/${customer.id}`}
                      className="text-primary-600 text-sm font-medium flex-shrink-0"
                      onClick={(e) => e.stopPropagation()}
                    >
                      View
                    </Link>
                  </div>
                </div>
              ))}
            </div>

            {/* Desktop Table View */}
            <div className="hidden md:block overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="py-3 px-4 w-10">
                      <div
                        className="cursor-pointer"
                        onClick={toggleSelectAll}
                      >
                        {selectedIds.length === customers.length ? (
                          <CheckSquare size={20} className="text-primary-600" />
                        ) : (
                          <Square size={20} className="text-gray-400" />
                        )}
                      </div>
                    </th>
                    <th className="text-left py-3 px-4 font-medium text-gray-700">Customer</th>
                    <th className="text-left py-3 px-4 font-medium text-gray-700">Address</th>
                    <th className="text-left py-3 px-4 font-medium text-gray-700">Area</th>
                    <th className="text-left py-3 px-4 font-medium text-gray-700">Plan</th>
                    <th className="text-left py-3 px-4 font-medium text-gray-700">Fiber Code</th>
                    <th className="text-right py-3 px-4 font-medium text-gray-700">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {customers.map((customer) => (
                    <tr
                      key={customer.id}
                      className={`border-t cursor-pointer transition-colors ${
                        selectedIds.includes(customer.id) ? 'bg-primary-50' : 'hover:bg-gray-50'
                      }`}
                      onClick={() => toggleSelect(customer.id)}
                    >
                      <td className="py-3 px-4">
                        {selectedIds.includes(customer.id) ? (
                          <CheckSquare size={20} className="text-primary-600" />
                        ) : (
                          <Square size={20} className="text-gray-400" />
                        )}
                      </td>
                      <td className="py-3 px-4">
                        <div>
                          <p className="font-medium text-gray-900">{customer.customerName}</p>
                          {customer.phone && <p className="text-sm text-gray-500">{customer.phone}</p>}
                        </div>
                      </td>
                      <td className="py-3 px-4 text-gray-700 max-w-xs">
                        <p className="truncate">{customer.fullAddress || '-'}</p>
                        {customer.pinLocation && (
                          <p className="text-xs text-gray-400 font-mono">GPS {customer.pinLocation}</p>
                        )}
                      </td>
                      <td className="py-3 px-4 text-gray-700">{customer.area || '-'}</td>
                      <td className="py-3 px-4 text-gray-700">{customer.cytaPlan || '-'}</td>
                      <td className="py-3 px-4">
                        {customer.cytaFiberCode ? (
                          <span className="font-mono text-sm text-green-700 bg-green-50 px-2 py-0.5 rounded">
                            {customer.cytaFiberCode}
                          </span>
                        ) : (
                          <span className="text-yellow-600 text-sm flex items-center gap-1">
                            <AlertCircle size={14} />
                            No code
                          </span>
                        )}
                      </td>
                      <td className="py-3 px-4 text-right">
                        <Link
                          to={`/customers/${customer.id}`}
                          className="text-primary-600 hover:text-primary-700 font-medium"
                          onClick={(e) => e.stopPropagation()}
                        >
                          View
                        </Link>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Footer */}
            <div className="px-4 py-3 border-t bg-gray-50 flex items-center justify-between">
              <p className="text-sm text-gray-700">
                {customers.length} customer(s) not processed
              </p>
              {selectedIds.length > 0 && (
                <p className="text-sm font-medium text-primary-600">
                  {selectedIds.length} selected
                </p>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default Agent;
